import { getEstimatedWait as getEstimatedWaitBase } from "@resto-guru/common-js";
import firebase from "datastore/database";
import uniqBy from "lodash/uniqBy";

import { setCustomer } from "api/customers";
import { getSectionAndTableByNumber, logTableTimeline } from "api/tables";
import { fetchWaitlistEntry } from "api/waitlist/fetchWaitlistEntry";
import { getTimeRange } from "util";
import { CUSTOMER_STATUS, TAG_TYPE, TIMELINE_TYPE } from "util/constants";
import getUrlBase from "datastore/getUrlBase";

import observe from "./observe";

const getCustomerTags = (tags) =>
  tags?.filter((tag) => tag.type === TAG_TYPE.CUSTOMER) ?? [];

const addWaitlistEntry = async (businessUid, data, id) => {
  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc(id)
    .set(data);
};

const updateWaitlistEntry = async (businessUid, entryUid, data) => {
  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc(entryUid)
    .update(data);
};

const remove = async (businessUid, uid) => {
  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc(uid)
    .delete();
};

export const getWaitlistRef = async (businessUid) => {
  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc();
};

async function getEstimatedWait(businessUid) {
  const estimatedWait = await getEstimatedWaitBase(firebase, businessUid);
  return getTimeRange(estimatedWait, businessUid);
}

const handleLogTableTimeline = (businessUid, booking, selectedDate) => {
  const promises = booking?.tableNumbers.map(async (tableNumber) => {
    const { table } = await getSectionAndTableByNumber(
      businessUid,
      tableNumber,
      selectedDate
    );
    logTableTimeline(businessUid, table.uid, {
      type: TIMELINE_TYPE.ASSIGN_TABLE_WAITLIST,
      data: booking,
    });
  });
  return Promise.all(promises);
};

// We want to remove extranous properties. It's not great to send color but I think we need it
const parseTags = (tags) =>
  tags.map((tag) => ({
    uid: tag.uid,
    color: tag.color,
    name: tag.name,
    type: tag.type,
  }));

export function getWaitlistData({
  comments,
  customerProfileNotes,
  durationOfBooking,
  name,
  phoneNumber,
  partySize,
  quotedTime,
  seatingPreferenceTags,
  tags,
  lastUpdate,
  tableNumbers,
}) {
  const arrivalTime = new Date();
  return {
    ...(comments && { comments }),
    ...(durationOfBooking && { durationOfBooking }),
    arrivalTime,
    createdAt: new Date(),
    name,
    partySize: Number(partySize),
    phoneNumber,
    quotedTime: Number(quotedTime),
    status: CUSTOMER_STATUS.BOOKED,
    ...(tableNumbers && { tableNumbers }),
    ...(tags && { tags }),
    ...(seatingPreferenceTags && { seatingPreferenceTags }),
    ...(customerProfileNotes != null && { customerProfileNotes }), // We want to write an empty string also
    lastUpdate,
  };
}

async function joinWaitlistNew(businessUid, entryUid, entry) {
  const url = `/business/${businessUid}/${entryUid}/waitlist`;
  let fullUrl = `${getUrlBase()}${url}`;
  try {
    const response = await fetch(fullUrl, {
      method: "POST",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
      },
      body: JSON.stringify(entry),
    });
    const json = await response.json();
    return json;
  } catch (error) {
    return null;
  }
}

const joinWaitlist = async (entry) => {
  async function addEntry() {
    try {
      const {
        name,
        businessUid,
        tags,
        phoneNumber,
        email,
        nationality,
        dob,
        gender,
        customerProfileNotes,
        selectedDate,
        entryUid,
      } = entry;
      const values = getWaitlistData(entry);
      const customerTags = getCustomerTags(tags);

      const customer = await setCustomer(
        businessUid,
        name,
        phoneNumber,
        email,
        customerTags,
        nationality,
        dob,
        gender,
        customerProfileNotes
      );
      const { uid } = customer;
      if (!uid) throw new Error("Couldn't add Customer to waitlist");

      const newTags = tags ? [...tags, ...customer.tags] : [...customer.tags];
      const uniqTags = uniqBy(newTags, "uid");

      const finalData = {
        ...values,
        customerUid: uid,
        tags: uniqTags,
      };
      await addWaitlistEntry(businessUid, finalData, entryUid);

      //Thiw moved to trigger!
      // Api.post(`/${businessUid}/waitlist/${entryUid}/sendWelcomeMessage`);
      // handleLogTableTimeline(
      //   businessUid,
      //   { ...values, customerUid: uid },
      //   selectedDate
      // );
    } catch (error) {
      throw new Error("Couldn't add Customer to waitlist");
    }
  }

  return addEntry();
};

const logSms = async (businessUid, entry) => {
  let notifications = [];
  if (entry.hasOwnProperty("notifications"))
    notifications = entry.notifications;
  notifications.push({ type: "sms", time: new Date() });

  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc(entry.uid)
    .update({ notifications });
};

const logCall = async (businessUid, entry) => {
  let notifications = [];
  if (!entry) return;
  if (entry.hasOwnProperty("notifications"))
    notifications = entry.notifications;
  notifications.push({ type: "call", time: new Date() });

  const logRef = firebase
    .firestore()
    .collection(`call-log/business/${businessUid}`)
    .doc();
  logRef.set({
    ...entry,
    time: new Date(),
    type: "waitlist",
  });

  return firebase
    .firestore()
    .collection(`waitlist/business/${businessUid}`)
    .doc(entry.uid)
    .update({ notifications });
};

const logStatusChange = async (
  businessUid,
  entryUid,
  status,
  source,
  lastUpdate
) => {
  const waitlistEntry = await fetchWaitlistEntry(businessUid, entryUid);
  let log = waitlistEntry.hasOwnProperty("log") ? waitlistEntry.log : [];
  log.push({
    timestamp: new Date(),
    uid: status,
    ...(source && { source: source }),
  });
  return updateWaitlistEntry(businessUid, entryUid, {
    log,
    status,
    ...(lastUpdate && { lastUpdate }),
  });
};

export {
  addWaitlistEntry,
  fetchWaitlistEntry,
  getEstimatedWait,
  joinWaitlist,
  joinWaitlistNew,
  logCall,
  logSms,
  logStatusChange,
  observe,
  remove,
  updateWaitlistEntry,
};
